import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LocalStorageCleanup } from '@/lib/localStorageCleanup';

export type UserRole = 'user' | 'admin';

export interface User {
    id: string;
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    profilePictureUrl?: string;
    avatar?: string;
    role: UserRole;
    bio?: string;
    skills?: string[];
    createdAt: string;
    isAdmin?: boolean;
}

interface AuthState {
    user: User | null;
    isAuthenticated: boolean; // This will be true if login/register was successful client-side
    loading: boolean;
    error: string | null;
    isVerifyingAuth: boolean; // To track initial auth verification

    // Auth actions
    login: (identifier: string, password: string) => Promise<boolean>;
    register: (username: string, email: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>; // Logout will now be async
    updateUser: (userData: Partial<User>) => void;
    updateAvatar: (avatar: string) => Promise<boolean>;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearAuthState: () => void;
    clearAuthStateWithCleanup: () => void;
    verifyAuth: () => Promise<void>; // New action to verify auth on load
}

// API Base URL (kept for reference, but not used in MVP client-side flow)
// const API_BASE_URL = '/api/user';

const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            isAuthenticated: false,
            loading: false,
            error: null,
            isVerifyingAuth: false,

            verifyAuth: async () => {
                const w = window as unknown as { __authVerifyInFlight?: boolean; __lastAuthVerifyAt?: number };
                const nowTs = Date.now();
                // Prevent spamming during HMR: bail if a verify is currently in flight or very recent
                if (w.__authVerifyInFlight || (w.__lastAuthVerifyAt && nowTs - w.__lastAuthVerifyAt < 2000)) {
                    return;
                }
                w.__authVerifyInFlight = true;
                set({ isVerifyingAuth: true, error: null });
                try {
                    const response = await fetch('/api/user/me', {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include', // Important for HttpOnly cookies
                    });

                    if (response.status === 401) {
                        // Not authenticated or session expired - clean up all user data
                        get().clearAuthStateWithCleanup();
                        set({ error: 'Your session has expired. Please log in again.', isAuthenticated: false, user: null });
                        // Throttle 401 broadcast to avoid repeated redirects/toasts
                        const w = window as unknown as { __lastAuth401At?: number };
                        const now = Date.now();
                        if (!w.__lastAuth401At || now - w.__lastAuth401At > 2000) {
                            try { window.dispatchEvent(new CustomEvent('app:auth-401')); } catch {
                                // Ignore dispatch errors
                            }
                            w.__lastAuth401At = now;
                        }
                        return;
                    }

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({ message: 'Failed to verify authentication status.' }));
                        get().clearAuthState();
                        set({ error: errorData.message || 'Failed to verify authentication status.', isAuthenticated: false, user: null });
                        return;
                    }

                    const data = await response.json();
                    if (data.success && data.user) {
                        const backendUser = data.user;
                        const appUser: User = {
                            id: backendUser.id,
                            username: backendUser.username,
                            email: backendUser.email,
                            firstName: backendUser.fullName?.split(' ')[0] || '',
                            lastName: backendUser.fullName?.split(' ').slice(1).join(' ') || '',
                            profilePictureUrl: backendUser.avatarUrl || backendUser.profilePictureUrl,
                            avatar: backendUser.avatarUrl || backendUser.avatar,
                            role: backendUser.role || 'user',
                            bio: backendUser.bio,
                            createdAt: backendUser.createdAt,
                            isAdmin: backendUser.isAdmin,
                        };
                        set({ user: appUser, isAuthenticated: true, error: null });
                    } else {
                        // Successful response but no user data or not success status in payload
                        get().clearAuthState();
                        set({ error: 'Authentication check failed. Please log in.', isAuthenticated: false, user: null });
                    }
                } catch (error: unknown) {
                    console.error('Verify auth API error:', error);
                    get().clearAuthState();
                    set({ error: (error as Error).message || 'An error occurred during authentication check.', isAuthenticated: false, user: null });
                } finally {
                    w.__authVerifyInFlight = false;
                    w.__lastAuthVerifyAt = Date.now();
                    set({ isVerifyingAuth: false });
                }
            },

            login: async (identifier, password) => {
                set({ loading: true, error: null, isVerifyingAuth: false }); // Reset isVerifyingAuth
                try {
                    const response = await fetch('/api/user/login', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ identifier, password }),
                    });
                    const data = await response.json();

                    if (!response.ok || !data.success) {
                        set({ loading: false, error: data.message || 'Login failed.' });
                        return false;
                    }

                    // Assuming the backend returns user object
                    // The user object from backend might have different field names (e.g. avatarUrl vs profilePictureUrl)
                    // Adjust mapping as necessary.
                    const backendUser = data.user;
                    const appUser: User = {
                        id: backendUser.id,
                        username: backendUser.username,
                        email: backendUser.email,
                        firstName: backendUser.fullName?.split(' ')[0] || '', // Example mapping
                        lastName: backendUser.fullName?.split(' ').slice(1).join(' ') || '', // Example mapping
                        profilePictureUrl: backendUser.avatarUrl, // Assuming backend sends avatarUrl
                        avatar: backendUser.avatarUrl, // Keep for compatibility if used elsewhere
                        role: backendUser.role || 'user', // Ensure role is handled
                        bio: backendUser.bio,
                        // skills: backendUser.skills, // If backend sends skills
                        createdAt: backendUser.createdAt,
                        isAdmin: backendUser.isAdmin,
                    };

                    set({
                        user: appUser,
                        isAuthenticated: true,
                        loading: false,
                    });
                    return true;
                } catch (error: unknown) {
                    console.error('Login API error:', error);
                    set({ loading: false, error: (error as Error).message || 'An error occurred during login.' });
                    return false;
                }
            },

            register: async (username, email, password) => {
                set({ loading: true, error: null, isVerifyingAuth: false }); // Reset isVerifyingAuth
                try {
                    const response = await fetch('/api/user/register', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ username, email, password, fullName: username }), // Sending username as fullName for now
                    });
                    const data = await response.json();

                    if (!response.ok || !data.success) {
                        set({ loading: false, error: data.message || 'Registration failed.' });
                        return false;
                    }

                    const backendUser = data.user;
                    const appUser: User = {
                        id: backendUser.id,
                        username: backendUser.username,
                        email: backendUser.email,
                        firstName: backendUser.fullName?.split(' ')[0] || '',
                        lastName: backendUser.fullName?.split(' ').slice(1).join(' ') || '',
                        profilePictureUrl: backendUser.avatarUrl, // Assuming backend might send this or it's derived
                        avatar: backendUser.avatarUrl,
                        role: backendUser.role || 'user',
                        bio: backendUser.bio,
                        createdAt: backendUser.createdAt,
                        isAdmin: backendUser.isAdmin,
                    };

                    set({
                        user: appUser,
                        isAuthenticated: true,
                        loading: false,
                    });
                    return true;
                } catch (error: unknown) {
                    console.error('Register API error:', error);
                    set({ loading: false, error: (error as Error).message || 'An error occurred during registration.' });
                    return false;
                }
            },

            logout: async () => {
                set({ loading: true, error: null, isVerifyingAuth: false });
                try {
                    await fetch('/api/user/logout', { method: 'POST', credentials: 'include' });
                } catch (e) {
                    console.error("Logout API call failed, clearing state anyway.", e);
                } finally {
                    // Clean up all user-specific localStorage data
                    LocalStorageCleanup.cleanupEverything();

                    set({
                        user: null,
                        isAuthenticated: false,
                        error: null,
                        loading: false,
                        isVerifyingAuth: false,
                    });
                }
            },

            updateUser: (userData) => {
                set(state => {
                    const updatedUser = state.user ? { ...state.user, ...userData } : null;
                    return { user: updatedUser };
                });
            },

            updateAvatar: async (avatar) => {
                try {
                    const response = await fetch('/api/me/avatar', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ avatar }),
                        credentials: 'include',
                    });
                    const data = await response.json();
                    if (!response.ok || !data.success) {
                        console.error('Failed to update avatar:', data.message);
                        return false;
                    }
                    set(state => ({
                        user: state.user ? { ...state.user, avatar, profilePictureUrl: avatar } : null,
                    }));
                    return true;
                } catch (error) {
                    console.error('Update avatar API error:', error);
                    return false;
                }
            },

            setLoading: (loading) => set({ loading }),
            setError: (error) => set({ error }),
            clearAuthState: () => set({ user: null, isAuthenticated: false, error: null, isVerifyingAuth: false }),
            clearAuthStateWithCleanup: () => {
                // Clean up all user-specific localStorage data when auth is invalidated
                LocalStorageCleanup.cleanupEverything();
                set({ user: null, isAuthenticated: false, error: null, isVerifyingAuth: false });
            }
        }),
        {
            name: 'syncreos-auth-storage',
            // Only persist user and isAuthenticated. Token is in HttpOnly cookie.
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
                // Do not persist isVerifyingAuth, error, or loading
            }),
            onRehydrateStorage: () => {
                // Trigger auth verification after state is rehydrated
                // This ensures we check with the backend even if persisted state says "authenticated"
                return (_, error) => {
                    if (error) {
                        console.error("Failed to rehydrate auth state:", error);
                        useAuthStore.getState().setError("Could not load session. Please try logging in.");
                        useAuthStore.getState().clearAuthStateWithCleanup(); // Ensure clean state and cleanup on rehydration error
                    }
                    // Do not auto-verify on rehydrate to avoid network spam during HMR.
                    // Protected routes will call verifyAuth lazily when needed.
                };
            }
        }
    )
);

// Initial call to verifyAuth when the store is first loaded/initialized in the app.
// This is an alternative to onRehydrateStorage if you prefer to call it explicitly
// from your main App component or a similar entry point.
// However, onRehydrateStorage is generally better for this specific use case with persist.
// useAuthStore.getState().verifyAuth(); // --> Moved to onRehydrateStorage

export default useAuthStore;